import { requireEnv } from '../../../core/utils/env';

/** Action enviada ao reCAPTCHA v3; o BFF recusa token gerado com outra (CAPTCHA_ACTION). */
export const CAPTCHA_ACTION = 'submit_ticket';

const SCRIPT_URL = 'https://www.google.com/recaptcha/api.js';
const LOAD_TIMEOUT_MS = 10000;

type Grecaptcha = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

/** Verificação anti-robô indisponível (script bloqueado, fora do ar ou sem resposta). */
export class CaptchaUnavailableError extends Error {
  constructor() {
    super('Não foi possível carregar a verificação anti-robô.');
    this.name = 'CaptchaUnavailableError';
  }
}

let loading: Promise<Grecaptcha> | undefined;

function getSiteKey(): string {
  return requireEnv('REACT_APP_CAPTCHA_SITE_KEY');
}

function injectScript(siteKey: string): Promise<Grecaptcha> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${SCRIPT_URL}?render=${encodeURIComponent(siteKey)}`;
    script.async = true;

    const fail = (): void => {
      window.clearTimeout(timeoutId);
      script.remove();
      reject(new CaptchaUnavailableError());
    };
    const timeoutId = window.setTimeout(fail, LOAD_TIMEOUT_MS);

    script.onerror = fail;
    script.onload = () => {
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha) {
        fail();
        return;
      }
      grecaptcha.ready(() => {
        window.clearTimeout(timeoutId);
        resolve(grecaptcha);
      });
    };

    document.head.appendChild(script);
  });
}

/**
 * Carrega o script do reCAPTCHA v3 uma única vez. Pode ser chamado antes do envio para adiantar o
 * download; se falhar, a próxima chamada tenta de novo.
 */
export function loadRecaptcha(): Promise<Grecaptcha> {
  if (!loading) {
    // Lê a site key dentro da promise: sem ela, a falha chega como rejeição, não como exceção síncrona.
    loading = new Promise<string>((resolve) => resolve(getSiteKey()))
      .then(injectScript)
      .catch((error: unknown) => {
        loading = undefined;
        throw error;
      });
  }
  return loading;
}

/**
 * Gera um token novo do reCAPTCHA v3 para o envio do ticket. O token vale 2 minutos e é de uso
 * único, por isso é gerado a cada tentativa, no clique em "Enviar". Falha fechada (RN03): qualquer
 * erro vira `CaptchaUnavailableError`, e o envio não acontece.
 */
export async function getCaptchaToken(): Promise<string> {
  const grecaptcha = await loadRecaptcha();
  try {
    return await grecaptcha.execute(getSiteKey(), { action: CAPTCHA_ACTION });
  } catch {
    throw new CaptchaUnavailableError();
  }
}
